import type { EventBattle } from '../../../types/Battle';
import type { AffiliateOverride, LegacyEventData } from '../../../types/LegacyEventData';
import type { Model } from '../../../types/Model';
import type { Product, ProductOverride } from '../../../types/Product';
import { ProductType, ApiProductType, VoucherType } from '../../../types/ProductTypes';
import type { Deal } from '../../types/Deal';
import type { DealData } from '../../types/DealData';
import type { ReviewData } from '../../types/ModelInfo';
import type { Widget } from '../../types/Widget';
import { WidgetType } from '../../types/WidgetType';

export const getVoucherType = (productType: ApiProductType): VoucherType | null => {
  switch (productType) {
    case ApiProductType.unique_code:
      return VoucherType.unique_code;
    case ApiProductType.vouchers:
      return VoucherType.voucher;
    case ApiProductType.rewards:
      return VoucherType.reward;
    case ApiProductType.newsletter:
      return VoucherType.newsletter;
    case ApiProductType.offer_deals:
      return VoucherType.deal;
    default:
      return null;
  }
};

export const getProductType = (productType: ApiProductType): ProductType => {
  switch (productType) {
    case ApiProductType.multimedia:
      return ProductType.multimedia;
    case ApiProductType.broadband:
      return ProductType.broadband;
    case ApiProductType.subscription:
      return ProductType.subscription;
    case ApiProductType.contracts:
      return ProductType.contract;
    case ApiProductType.deals:
      return ProductType.deal;
    case ApiProductType.accessories:
      return ProductType.accessory;
    case ApiProductType.unique_code:
    case ApiProductType.vouchers:
    case ApiProductType.rewards:
    case ApiProductType.newsletter:
    case ApiProductType.offer_deals:
    case ApiProductType.offers:
      return ProductType.offer;
    case ApiProductType.fallback:
      return ProductType.fallback;
    case ApiProductType.similar:
      return ProductType.similar;
    case ApiProductType.none:
      return ProductType.none;
    default:
      throw new Error(`Unknown product type: ${productType}`);
  }
};

const getProduct = (deal: Deal | undefined, deals: Deal[]): string => {
  const applicableDeal = deal ?? (deals[0] as Deal | undefined);
  const productType = applicableDeal?.product_type;
  return ApiProductType[productType ?? ApiProductType.none];
};

const completeProduct = (product: ProductOverride): Product => {
  const primary = product.product?.primary;

  return {
    product: {
      primary: {
        id: primary?.id ?? null,
        currency: primary?.currency ?? '',
        price: primary?.price ?? 0,
        previousPrice: primary?.previousPrice ?? null,
        name: primary?.name ?? '',
        labels: primary?.labels ?? [],
        preorder: primary?.preorder ?? false,
        link: primary?.link ?? '',
        revenueId: primary?.revenueId ?? null,
        startTime: primary?.startTime ?? null,
        endTime: primary?.endTime ?? null,
        type: primary?.type ?? ProductType.none,
        offerScope: primary?.offerScope ?? null,
        originalLink: primary?.originalLink ?? null,
        globalId: primary?.globalId ?? null,
        inStock: primary?.inStock ?? null,
        offerExclusive: primary?.offerExclusive ?? null,
        contractProvider: primary?.contractProvider ?? null,
        contractMinutes: primary?.contractMinutes ?? null,
        contractTexts: primary?.contractTexts ?? null,
        contractData: primary?.contractData ?? null,
        contractLength: primary?.contractLength ?? null,
        contractMonthlyPrice: primary?.contractMonthlyPrice ?? null,
        contractCurrency: primary?.contractCurrency ?? null,
        voucherCode: primary?.voucherCode ?? null,
        voucherAudience: primary?.voucherAudience ?? null,
        voucherPercentageSaving: primary?.voucherPercentageSaving ?? null,
        voucherMoneySaving: primary?.voucherMoneySaving ?? null,
        voucherType: primary?.voucherType ?? null,
      },
    },
    merchant: product.merchant ?? {},
    model: product.model ?? { id: null, brand: null, name: null, parent: null },
  };
};

export const getLegacyEventData = ({
  affiliate,
  category = 'Affiliates',
  product,
  products,
  dealData,
  deal,
  widget,
  widgetId,
  editorial,
  name,
  reviews,
  placeholder,
  star,
  blockLayout,
}: {
  affiliate: AffiliateOverride;
  category?: string;
  product?: ProductOverride;
  products?: ProductOverride[];
  dealData?: DealData;
  deal?: Deal;
  widget: Widget;
  widgetId: string;
  editorial: boolean;
  name?: string;
  reviews: ReviewData[];
  battle: EventBattle | null;
  placeholder: HTMLElement | null;
  language: string;
  star: string | null;
  blockLayout: string | null;
}): LegacyEventData => {
  const deals = dealData?.deals ?? [];
  const widgetType =
    star === 'hero' && widget.type === WidgetType.DEAL ? `Star ${widget.gaLabel}` : widget.gaLabel;

  const gaData: LegacyEventData = {
    category,
    affiliate: {
      action: {
        id: widgetId,
        timestamp: Date.now(),
        ...affiliate.action,
      },
      component: {
        flag: editorial ? 'Editor' : '',
        product: getProduct(deal, deals),
        category: name || widgetType, // Can't use ??, empty string is misused to signify unset data
        type: widget.type,
        label: ((): string => {
          if (deal) {
            return deal.model_matched.join(',');
          }

          if (deals.length > 0) {
            // Construct an array of model names,
            // based on the contents of model_matched for each deal
            const modelNames: Set<string> = new Set();
            deals.forEach((deal) => {
              deal.model_matched.forEach((modelName) => modelNames.add(modelName));
            });
            return Array.from(modelNames).join(',');
          }
          if (placeholder?.dataset.modelName) {
            return placeholder.dataset.modelName.replaceAll(',', '|');
          }
          return '';
        })(),
        // Set the order of the current event target within an array of all deals for the widget.
        // E.g., when the second merchant link item is clicked, the index is 1.
        index: deals.findIndex((d) => d.product_key === deal?.product_key),
        linkCount: deal ? 1 : deals.length,
        blockLayout,
        areaClicked: '',
        ...affiliate.component,
      },
    },
    // Ensure we only send data for 1 deal for click events (deal was provided)
    products: (deal ? [deal] : deals).map((deal) => {
      return {
        product: {
          primary: {
            id: deal.id,
            currency: deal.offer.currency_iso,
            price:
              deal.product_type !== ApiProductType.similar && deal.offer.price
                ? Number.parseFloat(deal.offer.price)
                : 0,
            previousPrice: deal.offer.was_price ?? null,
            contractProvider: deal.contract?.network ?? null,
            contractMinutes: deal.contract?.minutes ?? null,
            contractTexts: deal.contract?.texts ?? null,
            contractData: deal.contract?.data ?? null,
            contractLength: deal.contract?.length ?? null,
            contractMonthlyPrice:
              deal.contract?.monthly_price === undefined
                ? null
                : Number(deal.contract.monthly_price),
            contractCurrency: deal.offer.currency_iso,
            offerExclusive: deal.promos?.some((promo) => promo.type === 'exclusive') ?? null,
            voucherAudience: deal.offer.audience ?? null,
            voucherPercentageSaving: deal.offer.percentage_saving ?? null,
            voucherMoneySaving: deal.offer.money_saving ?? null,
            voucherType: getVoucherType(deal.product_type),
            name: deal.offer.name,
            labels: (deal.labels_formatted ?? []).map((label) => ({
              type: label.type,
              value: label.display_value,
            })),
            preorder: deal.offer.preorder ?? false,
            link: deal.offer.link,
            originalLink: deal.offer.rawLink ?? null,
            revenueId: deal.offer.custom_tracking_id ?? null,
            startTime: deal.start_time ?? null,
            endTime: deal.end_time ?? null,
            offerScope: deal.promos?.find((promo) => promo.type === 'scope')?.display_value ?? null,
            type: getProductType(deal.product_type),
            voucherCode: deal.promos?.find((p) => p.type === 'voucher_code')?.display_value ?? null,
            globalId: deal.product_key,
            inStock: deal.offer.in_stock,
          },
        },
        merchant: {
          id: deal.merchant.id,
          name: deal.merchant.name,
          url: deal.merchant.url,
          network: deal.an,
        },
        model: ((): Model => {
          const { brand, model_name, parents } = dealData?.modelInfo[deal.model_id] ?? {};
          return {
            id: deal.model_id,
            brand: brand ?? null,
            name: model_name ?? null,
            parent: parents?.[0]?.name ?? null,
          };
        })(),
      };
    }),
    reviews: reviews.map((review) => ({
      code: review.code_value,
      count: review.review_count,
      score: review.review_score,
    })),
  };

  if (products) {
    gaData.products = products.map(completeProduct);
  } else if (product) {
    // Extend existing products mapped from deals with explicitly passed product properties
    if (gaData.products.length === 0) {
      gaData.products = [completeProduct(product)];
    } else {
      gaData.products = gaData.products.map((existingProduct) => ({
        ...existingProduct,
        ...product,
        product: {
          ...existingProduct.product,
          ...product.product,
          primary: {
            ...existingProduct.product.primary,
            ...product.product?.primary,
          },
        },
      }));
    }
  }

  return gaData;
};
